import { MaybeRefOrGetter, computed, ref, toValue, watch } from "vue";

/**
 * Configuration options for the useFloatInput composable
 */
export interface UseFloatInputOptions {
  /**
   * The number of digits to display after the decimal point.
   * @default 2
   */
  digits?: number;

  /**
   * The maximum number that can be entered.
   * @default undefined
   */
  max?: number;

  /**
   * The minimum number that can be entered.
   * @default undefined
   */
  min?: number;

  /**
   * A callback that is called when the value is updated.
   * @default undefined
   */
  onUpdate?: (updatedValue: number) => void;
}

/**
 * Composable for handling float input fields with formatting and validation
 *
 * This composable provides:
 * 1. Automatic formatting of numeric values with specified decimal precision
 * 2. Min/max value constraints
 * 3. Conversion between number and string representations
 * 4. Validation on blur events
 *
 * In order to be reactive, make sure to pass the value as a ref or computed getter.
 *
 * @param {MaybeRefOrGetter<number | undefined>} value - The numeric value to bind to
 * @param {UseFloatInputOptions} options - Configuration options
 * @returns {Object} Reactive string value and event handlers
 */
export const useFloatInput = (
  value: MaybeRefOrGetter<number | undefined>,
  options: UseFloatInputOptions = {},
) => {
  // Extract options with defaults
  const {
    digits = 2, // Default to 2 decimal places
    onUpdate = () => {}, // Default to no-op update handler
  } = options;

  // Create a computed reference to the input value
  // This ensures reactivity regardless of how the value was passed in
  const valueRef = computed(() => toValue(value));

  // The string representation of the number value for display/input
  const stringValue = ref("");

  // Watch for changes to the numeric value and update the string representation
  watch(
    valueRef,
    (newValue) =>
      (stringValue.value =
        newValue === undefined || newValue === null
          ? "" // Empty string for null/undefined values
          : newValue.toFixed(digits)), // Format number with specified decimal places
    { immediate: true }, // Run immediately to set initial value
  );

  /**
   * Handles the blur event on the input field
   *
   * This function:
   * 1. Parses the current string value to a number
   * 2. Applies min/max constraints if configured
   * 3. Reformats the value with the correct number of decimal places
   * 4. Calls the onUpdate callback with the new value
   */
  const handleBlur = () => {
    // Parse the current string value to a number
    const parsedValue = parseFloat(stringValue.value);

    // Apply constraints and formatting
    if (options.max !== undefined && parsedValue > options.max) {
      // Clamp to maximum value
      stringValue.value = options.max.toFixed(digits);
    } else if (options.min !== undefined && parsedValue < options.min) {
      // Clamp to minimum value
      stringValue.value = options.min.toFixed(digits);
    } else if (!isNaN(parsedValue)) {
      // Format valid number with correct decimal places
      stringValue.value = parsedValue.toFixed(digits);
    } else {
      // Clear invalid input
      stringValue.value = "";
    }

    // Notify about the update
    onUpdate(parsedValue);
  };

  // Return the public API of the composable
  return {
    stringValue, // Reactive string representation for v-model binding
    handleBlur, // Event handler for input blur events
  };
};
